import { useState, useEffect } from 'react';

/**
 * Tailwind CSS Breakpoints (default)
 */
const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Custom hook to detect if viewport is above a specified breakpoint
 * Works on initial load and automatically updates on window resize
 * 
 * @param {string} breakpoint - Breakpoint name ('sm', 'md', 'lg', 'xl', '2xl')
 * @returns {boolean} - True if viewport width is >= breakpoint
 * 
 * @example
 * const isAboveSm = useBreakpoint('sm');
 * const isAboveMd = useBreakpoint('md');
 * 
 * // Use in component
 * if (isAboveSm) {
 *   // Show desktop layout
 * } else {
 *   // Show mobile layout
 * }
 * 
 * // Use for table column
 * const columns = [
 *   {
 *     title: 'Actions',
 *     key: 'actions',
 *     fixed: isAboveSm ? 'right' : undefined,
 *     ...
 *   }
 * ];
 */
export function useBreakpoint(breakpoint) {
  const [isAbove, setIsAbove] = useState(() => {
    // Initialize with current window width
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      const breakpointValue = breakpoints[breakpoint];
      return breakpointValue ? width >= breakpointValue : false;
    }
    return false;
  });

  useEffect(() => {
    const breakpointValue = breakpoints[breakpoint];
    
    if (!breakpointValue) {
      console.warn(`useBreakpoint: Invalid breakpoint "${breakpoint}". Use: sm, md, lg, xl, 2xl`);
      return;
    }

    // Handler to check viewport width
    const handleResize = () => {
      const width = window.innerWidth;
      setIsAbove(width >= breakpointValue);
    };

    // Check on mount
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint]);

  return isAbove;
}

/**
 * Hook to get all breakpoint states at once
 * @returns {Object} - Object with all breakpoint states
 * 
 * @example
 * const breakpoints = useBreakpoints();
 * console.log(breakpoints.sm); // true/false
 * console.log(breakpoints.md); // true/false
 * console.log(breakpoints.lg); // true/false
 */
export function useBreakpoints() {
  const sm = useBreakpoint('sm');
  const md = useBreakpoint('md');
  const lg = useBreakpoint('lg');
  const xl = useBreakpoint('xl');
  const xl2 = useBreakpoint('2xl');

  return {
    sm,
    md,
    lg,
    xl,
    '2xl': xl2,
  };
}

export default useBreakpoint;
